import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Virtuoso } from 'react-virtuoso'
import {
    closestCenter,
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Box, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { useGetQuoteItemsQuery, useUpdateQuoteItemsMutation } from '@/app/services/quoteItems'
import { useCalculateQuoteMutation, useGetQuoteQuery, useUpdateQuoteMutation } from '@/app/services/quotes'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import {
    selectFilteredItemsIds,
    selectFilterOption,
    selectOpenDrawingDoctorModal,
    selectOpenPartQuoteHistoryModal,
    selectSearchQuery,
    selectSelectedPartFromLibrary,
    setFilteredItemsIds,
    setFilterOption,
    setIsCalculatingQuote,
    setOpenDrawingDoctorModal,
    setOpenPartQuoteHistoryModal,
    setSelectedPartFromLibrary,
    setSelectedQuoteItemDrawingId,
    setSelectedQuoteStatus,
} from '@/app/slices/quoteItemsSlice'
import { useMutationErrorSnackbar } from '@/common/hooks'
import { QUOTE_STATUS_ARRAY } from '@/common/utils'
import {
    filterQuoteItemsBySearch,
    quoteItemErrors,
    quoteItemHasIssues,
    quoteItemUnset,
    quoteItemWarnings,
} from '@/common/utils/quoteUtilities'
import PartQuoteHistoryDialog from '@/features/customer-central/components/Tabs/PartLibraryTab/PartQuoteHistoryDialog'

import DrawingDoctorModal from '../DrawingDoctorModal/DrawingDoctorModal'

import QuoteItem from './ItemContent/QuoteItem'
import QuoteItemOverlay from './QuoteItemOverlay'
import SeekQuoteItemOverlay from './SeekQuoteItemOverlay'

/**
 * Custom List component for Virtuoso with flex layout styling
 * Extracted outside QuoteItemList to avoid re-creation on each render (React 19 ref as prop)
 */
const VirtuosoList = ({ ref = null, sx = null, ...rest }) => {
    return (
        <Box
            {...rest}
            ref={ref}
            sx={[
                {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        />
    )
}

VirtuosoList.propTypes = {
    ref: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
    sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
}

/**
 * QuoteItemList - The quote's items as a sortable, virtualised list.
 *
 * @param {Object} props
 * @param {boolean} [props.showAttachments] - Shows each item's documents control; read once by the quote screen
 */
const QuoteItemList = ({ showAttachments = false }) => {
    const { t } = useTranslation()
    const notifyMutationError = useMutationErrorSnackbar()
    const dispatch = useDispatch()
    const { quoteId } = useParams()

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    const organisationId = useSelector(selectOrganisationId)

    const isDrawingDoctorModalOpen = useSelector(selectOpenDrawingDoctorModal)
    const isPartQuoteHistoryModalOpen = useSelector(selectOpenPartQuoteHistoryModal)
    const selectedPartFromLibrary = useSelector(selectSelectedPartFromLibrary)
    const filterOption = useSelector(selectFilterOption)
    const searchQuery = useSelector(selectSearchQuery)
    const quoteItemsIds = useSelector(selectFilteredItemsIds)

    const { data: selectedQuote } = useGetQuoteQuery({ organisationId, quoteId })
    const { data: quoteItems, isFetching: isFetchingQuoteItems } = useGetQuoteItemsQuery({
        organisationId,
        quoteId,
    })

    const [calculateQuoteMutation, { isLoading: isCalculating }] = useCalculateQuoteMutation({
        fixedCacheKey: 'shared-calculate-quote',
    })

    const [updateQuoteItems] = useUpdateQuoteItemsMutation()
    const [updateQuote] = useUpdateQuoteMutation()

    const [activeItemId, setActiveItemId] = useState(null)
    const [activeItemName, setActiveItemName] = useState(null)

    const onDragStart = (event) => {
        const id = event.active.id
        setActiveItemId(id)

        const activeItem = quoteItems.find((item) => item?.id === id)
        setActiveItemName(activeItem.name)
    }

    const handleUpdateQuote = useCallback(
        async (updatedFields) => {
            try {
                await updateQuote({
                    organisationId,
                    quoteId,
                    quote: {
                        ...selectedQuote,
                        ...updatedFields,
                    },
                }).unwrap()
            } catch (error) {
                notifyMutationError(error, t('$t(An error occurred) updating the $t(quote).'))
            }
        },
        [organisationId, quoteId, selectedQuote, t, notifyMutationError, updateQuote]
    )

    const handleUpdateQuoteItems = useCallback(
        async (sortedList) => {
            try {
                await updateQuoteItems({
                    organisationId,
                    quoteId,
                    quoteItems: sortedList,
                }).unwrap()
            } catch (error) {
                notifyMutationError(error, t('Failed to reorder $t(quote) items. Undoing changes'))
            } finally {
                setActiveItemId(null)
            }
        },
        [organisationId, quoteId, t, notifyMutationError, updateQuoteItems]
    )

    const handleDrawingCleanerClose = () => {
        dispatch(setOpenDrawingDoctorModal(false))
        dispatch(setSelectedQuoteItemDrawingId(null))
        dispatch(setSelectedQuoteStatus(QUOTE_STATUS_ARRAY.NotCalculated))
        if (selectedQuote.status !== QUOTE_STATUS_ARRAY.NotCalculated) {
            dispatch(setSelectedQuoteStatus(QUOTE_STATUS_ARRAY.NotCalculated))
            handleUpdateQuote({ status: QUOTE_STATUS_ARRAY.NotCalculated })
        }
    }

    const handleClosePartQuoteHistoryModal = () => {
        dispatch(setOpenPartQuoteHistoryModal(false))
        dispatch(setSelectedPartFromLibrary(null))
    }

    const onDragEnd = useCallback(
        (event) => {
            const { active, over } = event

            if (!active || !over) {
                return
            }

            if (active.id === over.id) {
                return
            }

            const oldIndex =
                active?.data?.current?.sortable?.index ?? quoteItems.find((item) => item?.id === active.id)?.index
            const newIndex = over.data.current.sortable.index

            const items = structuredClone(arrayMove(quoteItems, oldIndex, newIndex))

            items?.forEach((item, index) => {
                item.index = index
            })

            if (selectedQuote.sortQuoteItemsBy !== 'Custom') {
                handleUpdateQuote({ sortQuoteItemsBy: 'Custom', sortQuoteItemsAsc: true })
            }
            handleUpdateQuoteItems(items)
        },
        [quoteItems, handleUpdateQuote, selectedQuote, handleUpdateQuoteItems]
    )

    const statusFilteredItems = useMemo(() => {
        if (filterOption === 'showAllItems') {
            return quoteItems
        }

        return quoteItems.filter((quoteItem) => {
            if (filterOption.includes('showUnsetItems') && quoteItemUnset(quoteItem)) {
                return true
            }

            if (
                filterOption.includes('showWarningItems') &&
                !quoteItemUnset(quoteItem) &&
                quoteItemWarnings(quoteItem)
            ) {
                return true
            }

            if (filterOption.includes('showErrorItems') && !quoteItemUnset(quoteItem) && quoteItemErrors(quoteItem)) {
                return true
            }

            if (
                filterOption.includes('showSuccessItems') &&
                !quoteItemHasIssues(quoteItem) &&
                !quoteItemUnset(quoteItem)
            ) {
                return true
            }

            return false
        })
    }, [quoteItems, filterOption])

    const filteredQuoteItems = useMemo(
        () => filterQuoteItemsBySearch(statusFilteredItems, searchQuery),
        [statusFilteredItems, searchQuery]
    )

    const hasActiveSearch = Boolean(searchQuery?.trim())
    const showNoSearchResults = hasActiveSearch && filteredQuoteItems?.length === 0

    const calculateQuote = useCallback(async () => {
        try {
            await calculateQuoteMutation({ organisationId, quoteId }).unwrap()
        } catch (error) {
            notifyMutationError(error, t(error.data))
        }
    }, [organisationId, quoteId, t, notifyMutationError, calculateQuoteMutation])

    useEffect(() => {
        // Reset the status filter only when the status filter itself yields no items.
        // A zero-match search must not clear the user's active status filter.
        if (!filterOption.includes('showAllItems') && statusFilteredItems.length <= 0) {
            dispatch(setFilterOption('showAllItems'))
        }

        dispatch(setFilteredItemsIds(filteredQuoteItems?.map((item) => item.id)))
    }, [filteredQuoteItems, statusFilteredItems, filterOption, dispatch])

    useEffect(() => {
        dispatch(setIsCalculatingQuote(isCalculating))
    }, [isCalculating, dispatch])

    useEffect(() => {
        if (organisationId && quoteId && [QUOTE_STATUS_ARRAY.Draft].includes(selectedQuote?.status)) {
            calculateQuote()
        }
    }, [organisationId, quoteId, selectedQuote?.status, calculateQuote])

    // Note: Initial loading is handled by parent Quote component
    // This component only renders when data is available
    // The opacity fade handles refetch scenarios to keep DOM stable and prevent insertBefore errors
    return (
        <Box sx={{ position: 'relative', opacity: isFetchingQuoteItems ? 0.6 : 1, transition: 'opacity 0.2s' }}>
            <DndContext
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                sensors={sensors}
                onDragEnd={onDragEnd}
                onDragStart={onDragStart}
            >
                <SortableContext
                    items={quoteItemsIds ?? []}
                    strategy={verticalListSortingStrategy}
                >
                    {showNoSearchResults ? (
                        <Typography
                            color="text.secondary"
                            sx={{ py: 4, textAlign: 'center' }}
                            variant="body1"
                        >
                            {t('No parts match your search')}
                        </Typography>
                    ) : (
                        <Virtuoso
                            components={{
                                ScrollSeekPlaceholder: SeekQuoteItemOverlay,
                                List: VirtuosoList,
                            }}
                            data={filteredQuoteItems}
                            defaultItemHeight={510}
                            increaseViewportBy={500}
                            itemContent={(index, quoteItem) => (
                                <QuoteItem
                                    id={quoteItem?.id}
                                    key={quoteItem?.id}
                                    showAttachments={showAttachments}
                                />
                            )}
                            key={quoteId}
                            scrollSeekConfiguration={{
                                enter: (velocity) => Math.abs(velocity) > 600,
                                exit: (velocity) => Math.abs(velocity) < 100,
                            }}
                            style={{ height: '100%' }}
                            useWindowScroll
                        />
                    )}
                </SortableContext>
                <DragOverlay>
                    {activeItemId ? (
                        <QuoteItemOverlay
                            id={activeItemId}
                            name={activeItemName}
                        />
                    ) : null}
                </DragOverlay>
            </DndContext>

            {isDrawingDoctorModalOpen ? (
                <DrawingDoctorModal
                    open={isDrawingDoctorModalOpen}
                    onClose={handleDrawingCleanerClose}
                />
            ) : null}

            {isPartQuoteHistoryModalOpen ? (
                <PartQuoteHistoryDialog
                    handleClose={handleClosePartQuoteHistoryModal}
                    open={isPartQuoteHistoryModalOpen}
                    partLibrary={selectedPartFromLibrary}
                />
            ) : null}
        </Box>
    )
}

QuoteItemList.propTypes = {
    showAttachments: PropTypes.bool,
}

export default memo(QuoteItemList)
